import { useCallback, useEffect, useRef, RefObject } from 'react';
import type { DragMoveEvent, DragStartEvent } from '@dnd-kit/react';

type AnyDragEvent = DragStartEvent | DragMoveEvent;

/** Height of the band at each edge of the scroll viewport that triggers auto-scroll. */
const EDGE_SIZE = 64;
/** Maximum scroll speed (px per animation frame) reached at the very edge. */
const MAX_SPEED = 18;

const isScrollable = (el: Element): boolean => {
    const { overflowY } = getComputedStyle(el);
    return (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
        el.scrollHeight > el.clientHeight;
};

/**
 * Nearest scrollable ancestor of `start`, crossing shadow boundaries (the app can be mounted inside
 * a host's shadow root). Falls back to the document scroller (the window) when none is found.
 */
const findScrollContainer = (start: Element | null): Element => {
    let el: Element | null = start;
    while (el) {
        if (el !== document.documentElement && el !== document.body && isScrollable(el)) {
            return el;
        }
        const root = el.getRootNode();
        el = el.parentElement ?? (root instanceof ShadowRoot ? root.host : null);
    }
    return document.scrollingElement ?? document.documentElement;
};

const getPointerY = (event: AnyDragEvent): number | null => {
    const native = event.nativeEvent as PointerEvent | undefined;
    if (native && Number.isFinite(native.clientY)) {
        return native.clientY;
    }
    const current = event.operation?.position?.current;
    return current ? current.y : null;
};

/**
 * Auto-scrolls the list while dragging when it doesn't fit on screen. dnd-kit v0.5's built-in
 * AutoScroller does not scroll the window in this app (it only drives real scroll containers, and
 * here the app scrolls the window from inside a shadow root), and its drag events stop firing while
 * the pointer is held still. So this runs an rAF loop that reads the live pointer Y (seeded on
 * `onDragStart`, updated via `onDragMove`) and scrolls the nearest scrollable ancestor — the window
 * or an embedding host's container — whenever the pointer is near the top/bottom edge.
 *
 * Returns handlers to wire onto `DragDropProvider`, plus `stop` to fold into `onDragEnd`.
 */
export const useDragScroll = (targetRef: RefObject<HTMLElement | null>) => {
    const pointerYRef = useRef(0);
    const rafRef = useRef<number | null>(null);
    const containerRef = useRef<Element | null>(null);

    const stop = useCallback(() => {
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    }, []);

    useEffect(() => stop, [stop]);

    const step = useCallback(() => {
        const container = containerRef.current;
        if (container === null) {
            return;
        }
        const isWindow = container === document.scrollingElement ||
            container === document.documentElement ||
            container === document.body;
        const rect = isWindow
            ? { top: 0, bottom: window.innerHeight }
            : container.getBoundingClientRect();
        const top = rect.top + EDGE_SIZE;
        const bottom = rect.bottom - EDGE_SIZE;
        const y = pointerYRef.current;

        let delta = 0;
        if (y < top) {
            delta = -MAX_SPEED * Math.min(1, (top - y) / EDGE_SIZE);
        } else if (y > bottom) {
            delta = MAX_SPEED * Math.min(1, (y - bottom) / EDGE_SIZE);
        }
        if (delta !== 0) {
            container.scrollTop += delta;
        }
        rafRef.current = requestAnimationFrame(step);
    }, []);

    const onDragStart = useCallback((event: DragStartEvent) => {
        const y = getPointerY(event);
        if (y !== null) {
            pointerYRef.current = y;
        }
        containerRef.current = findScrollContainer(targetRef.current);
        if (rafRef.current === null) {
            rafRef.current = requestAnimationFrame(step);
        }
    }, [targetRef, step]);

    const onDragMove = useCallback((event: DragMoveEvent) => {
        const y = getPointerY(event);
        if (y !== null) {
            pointerYRef.current = y;
        }
    }, []);

    return { onDragStart, onDragMove, stop };
};
